import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Toast from '../common/Toast';
import { useInvalidateGoogleDriveStatus } from '../hooks/useGoogleDriveIntegration';

/**
 * Google Drive OAuth sends the browser back to the page it started on with `?googleDrive=connected|error`.
 * Show the result once, refresh the connection status, and drop the params from the URL.
 */
const GoogleDriveConnectNotice: React.FC = () => {
    const { t } = useTranslation('people');
    const location = useLocation();
    const navigate = useNavigate();
    const invalidateStatus = useInvalidateGoogleDriveStatus();
    const [notice, setNotice] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const result = params.get('googleDrive');
        if (!result) return;
        const reason = params.get('reason');

        if (result === 'connected') {
            setNotice({
                type: 'success',
                message: t(
                    'userProfile.integrations.googleDrive.connectedNotice',
                    'Google Drive connected. You can now send documents to Google Drive from the export menu.'
                ),
            });
        } else if (reason === 'access_denied') {
            setNotice({
                type: 'error',
                message: t('userProfile.integrations.googleDrive.cancelledNotice', 'Google Drive connection was cancelled.'),
            });
        } else if (reason === 'drive_permission_not_granted') {
            setNotice({
                type: 'error',
                message: t(
                    'userProfile.integrations.googleDrive.permissionNotice',
                    'Google Drive was not connected because Drive access was not allowed. Connect again and allow access to Google Drive.'
                ),
            });
        } else {
            setNotice({
                type: 'error',
                message: t('userProfile.integrations.googleDrive.failedNotice', 'Could not connect Google Drive. Please try again.'),
            });
        }
        void invalidateStatus();

        params.delete('googleDrive');
        params.delete('reason');
        const qs = params.toString();
        navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- react to OAuth landing params only
    }, [location.search]);

    if (!notice) return null;
    // Errors stay until dismissed; success auto-hides
    return (
        <Toast
            message={notice.message}
            type={notice.type}
            persistent={notice.type === 'error'}
            onClose={() => setNotice(null)}
        />
    );
};

export default GoogleDriveConnectNotice;
